import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Synchronous function to get user display name from user data object
 * @param {Object} userData - User data object (from Firestore document or user object)
 * @returns {string} - Properly formatted display name
 */
export const getUserDisplayName = (userData) => {
  if (!userData) return 'Unknown User';

  // Try fullName first (some users might have this field)
  if (userData.fullName && userData.fullName.trim() && !userData.fullName.includes('@') && userData.fullName !== userData.email?.split('@')[0]) {
    return userData.fullName.trim();
  }
  
  // Create proper display name using priority logic
  if (userData.firstName && userData.lastName) {
    return `${userData.firstName.trim()} ${userData.lastName.trim()}`;
  } else if (userData.firstName) {
    return userData.firstName.trim();
  } else if (userData.displayName && userData.displayName.trim() && !userData.displayName.includes('@')) {
    return userData.displayName.trim();
  } else if (userData.email) {
    return userData.email.split('@')[0];
  } else {
    return 'Unknown User';
  }
};

/**
 * Async function to get user display name with database lookup
 * @param {Object} currentUser - User object (can be from auth context or user document)
 * @param {Object} db - Firestore database instance
 * @returns {Promise<string>} - Properly formatted display name
 */
export const getUserDisplayNameAsync = async (currentUser, db) => {
  if (!currentUser?.uid || !db) return 'You';

  try {
    const userRef = doc(db, 'c3_users', currentUser.uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      return getUserDisplayName(userData);
    }
  } catch (error) {
    console.error('Error fetching user display name:', error);
  }
  
  return 'You';
};

/**
 * Get display name for a message author with real-time lookup
 * @param {Object} message - Message object with authorId, firstName, lastName, authorName
 * @param {Object} db - Firestore database instance
 * @returns {Promise<string>} - Properly formatted author display name
 */
export const getMessageAuthorDisplayName = async (message, db) => {
  // First try to use the stored firstName + lastName if both are available
  if (message.firstName && message.lastName && message.firstName.trim() && message.lastName.trim()) {
    return `${message.firstName.trim()} ${message.lastName.trim()}`;
  }

  // IMPORTANT: Avoid cross-user Firestore reads to prevent permission errors in non-admin sessions.
  // Rely on message fields that are already included in chat payload.

  // Fallback to the stored message data with improved logic
  if (message.firstName && message.firstName.trim()) {
    return message.firstName.trim();
  } else if (message.authorName && message.authorName.includes('@')) {
    return message.authorName.split('@')[0];
  } else if (message.authorName) {
    return message.authorName;
  } else {
    return 'Unknown User';
  }
};

/**
 * Create proper display name for message creation
 * @param {Object} userData - User data from Firestore
 * @returns {string} - Properly formatted display name
 */
export const createMessageAuthorName = (userData) => {
  // Try fullName first
  if (userData.fullName && userData.fullName.trim() && !userData.fullName.includes('@') && userData.fullName !== userData.email?.split('@')[0]) {
    return userData.fullName.trim();
  } else if (userData.firstName && userData.lastName) {
    return `${userData.firstName.trim()} ${userData.lastName.trim()}`;
  } else if (userData.firstName) {
    return userData.firstName.trim();
  } else if (userData.displayName && userData.displayName.trim() && !userData.displayName.includes('@')) {
    return userData.displayName.trim();
  } else if (userData.email) {
    // Use email username as last resort
    return userData.email.split('@')[0];
  } else {
    return 'Unknown User';
  }
};

/**
 * React component to display current user's full name
 */

export const CurrentUserDisplay = ({ currentUser, db, className = "mb-2 text-sm text-gray-600" }) => {
  const [userDisplayName, setUserDisplayName] = useState('You');

  useEffect(() => {
    const fetchDisplayName = async () => {
      const displayName = await getUserDisplayNameAsync(currentUser, db);
      setUserDisplayName(displayName);
    };

    fetchDisplayName();
  }, [currentUser?.uid, db]);

  return (
    <div className={className}>
      {userDisplayName}
    </div>
  );
};

/**
 * React component to display message author name with real-time user lookup
 */
export const MessageAuthorName = ({ message, db, className = "font-semibold text-[#39325b]" }) => {
  const [authorDisplayName, setAuthorDisplayName] = useState('Loading...');

  useEffect(() => {
    const fetchAuthorName = async () => {
      const displayName = await getMessageAuthorDisplayName(message, db);
      setAuthorDisplayName(displayName);
    };

    fetchAuthorName();
  }, [message.authorId, message.firstName, message.lastName, message.authorName, db]);

  return (
    <span className={className}>
      {authorDisplayName}
    </span>
  );
};

/**
 * React component for typing indicator
 */
export const TypingIndicator = ({ currentUser, db, className = "text-xs text-gray-500" }) => {
  const [userDisplayName, setUserDisplayName] = useState('You');

  useEffect(() => {
    const fetchDisplayName = async () => {
      const displayName = await getUserDisplayNameAsync(currentUser, db);
      setUserDisplayName(displayName);
    };

    fetchDisplayName();
  }, [currentUser?.uid, db]);

  return (
    <span className={className}>
      {userDisplayName} is typing...
    </span>
  );
}; 